import * as Redux from "redux";
import * as _ from "lodash";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { OrderActions } from "src/core/orders/store/orders.actions";
import * as AT from "src/core/orders/store/orders.constants";
import {
  IOrder,
  IOrderLine
} from "src/core/orders/api/orders.api";
import {
  formatOrderLineFromPrice,
  recalculateOrderLine
} from "src/core/prices/utils";
import { gtmEvent } from "src/library/utils/tagmanager-events";
import { orderLineToGTMItem } from "src/core/products/utils/product-to-gtm-item";
import { IProduct } from "src/core/products/api/products.api";
import { IProductSubstitution } from "src/modules/substitutions/api/substitutions.api";

import { ICurrentOrder } from "../api/orders.api";

export interface IOrderStore {
  currentOrder: ICurrentOrder;
  contextFilters?: FilterParams<IOrder>;
  allOrdersErrorsCount: number;
}

export const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 10,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

export const orderInitialState: IOrderStore = {
  currentOrder: {
    data: null,
    mode: "view",
    saved: true,
    allowedDays: null
  },

  contextFilters: {},

  allOrdersErrorsCount: 0
};

const changeProductQuantity = (orderLines: IOrderLine[], product: IProduct, quantity: number, discountValue = 0, substitution?: IProductSubstitution): IOrderLine[] => {
  const prodIndex = _.findIndex(orderLines, line => {
    return line.productId === product.id;
  });

  const existingPrice = product.prices && product.prices[ 0 ];

  // amend existing product
  if (prodIndex !== -1) {
    const subOrderLineToUpdate = orderLines[ prodIndex ].substituteOrderLine;
    const orderLineToUpdate = orderLines[ prodIndex ];

    // update replacement product (if present) as well of original
    if (subOrderLineToUpdate) {
      const subOrderLineValues = recalculateOrderLine({
        ...subOrderLineToUpdate,
        quantity
      });

      subOrderLineToUpdate.totalPrice = subOrderLineValues.totalPrice;
      subOrderLineToUpdate.totalVatValue = subOrderLineValues.totalVatValue;
      subOrderLineToUpdate.quantity = quantity;
    } else if (substitution) {
      const existingSubPrice = substitution.replacementProduct.prices && substitution.replacementProduct.prices[ 0 ];

      if (existingSubPrice) {
        // keep the substitute up to date with the original
        orderLineToUpdate.substituteOrderLine = formatOrderLineFromPrice(existingSubPrice, {
          productId: substitution.replacementProduct.id,
          quantity,
          product: substitution.replacementProduct
        }, discountValue);
      }
    }

    if (existingPrice) {
      const newOrderLineValues = formatOrderLineFromPrice(existingPrice, {
        productId: product.id,
        quantity,
        product
      }, discountValue) as IOrderLine;

      orderLineToUpdate.quantity = quantity;
      orderLineToUpdate.price = newOrderLineValues.price;
      orderLineToUpdate.totalPrice = newOrderLineValues.totalPrice;
      orderLineToUpdate.totalVatValue = newOrderLineValues.totalVatValue;
    }

    if (quantity === 0) {
      gtmEvent("remove_from_cart", {
        ecommerce: {
          currency: "GBP",
          value: orderLineToUpdate.totalPrice || 0,
          items: [ orderLineToGTMItem(orderLineToUpdate, { quantity: 0 }) ]
        }
      });
    }
  } else {
    if (existingPrice) {
      // ensure the price on the original order line
      const newOrderLine = formatOrderLineFromPrice(existingPrice, {
        productId: product.id,
        quantity,
        product
      }, discountValue) as IOrderLine;

      gtmEvent("add_to_cart", {
        ecommerce: {
          currency: "GBP",
          value: newOrderLine.totalPrice || 0,
          items: [ orderLineToGTMItem(newOrderLine) ]
        }
      });

      if (substitution) {
        const replacementProduct = substitution.replacementProduct;

        newOrderLine.productSubstitution = substitution;

        if (replacementProduct) {
          const existingSubPrice = replacementProduct.prices && replacementProduct.prices[ 0 ];

          if (existingSubPrice) {
            // keep the substitute up to date with the original
            newOrderLine.substituteOrderLine = formatOrderLineFromPrice(existingSubPrice, {
              productId: replacementProduct.id,
              quantity,
              product: replacementProduct
            }, discountValue);
          }
        }
      }

      if (quantity > 0) {
        // new product insertion
        orderLines.push(
          newOrderLine
        );
      }
    }
  }

  return _.filter(orderLines, ol => ol.quantity > 0);
};

export const orders: Redux.Reducer<IOrderStore> = (state: IOrderStore = orderInitialState, action: OrderActions): IOrderStore => {
  // handle the data structure change to orders
  if (!state.currentOrder) {
    state = {
      ...state,
      currentOrder: orderInitialState.currentOrder
    };
  }

  switch (action.type) {
    case AT.UPDATE_ORDER_LINE: {
      if (!state.currentOrder.data) {
        return state;
      }

      const newOrderLines = changeProductQuantity(state.currentOrder.data?.orderLines || [], action.product, action.newQuantity, state.currentOrder.data.branch?.discount, action.substitution);

      return {
        ...state,
        currentOrder: {
          ...state.currentOrder,
          data: {
            ...state.currentOrder.data,
            orderLines: newOrderLines
          }
        }
      };
    }

    case AT.SET_CURRENT_ORDER:
      return {
        ...state,
        currentOrder: action.order === null ? orderInitialState.currentOrder : action.order
      };

    case AT.SET_CURRENT_ORDER_MODE:
      return {
        ...state,
        currentOrder: {
          ...state.currentOrder,
          mode: action.mode || "view"
        }
      };
    case AT.SET_ORDERS_WITH_ERRORS:
      return {
        ...state,
        allOrdersErrorsCount: action.allOrdersWithErrors
      };

    case AT.UPDATE_CUSTOMER_REFERENCE:
      if (state.currentOrder.data) {
        return {
          ...state,
          currentOrder: {
            ...state.currentOrder,
            saved: false,
            mode: "edit",
            data: {
              ...state.currentOrder.data,
              customerReference: action.reference
            }
          }
        };
      }

      return state;
    case AT.PURGE_ORDERS:
      return orderInitialState;
    default:
      return state;
  }
};
